import { type Translate, useTranslation } from "@redotech/redo-i18n";
import { memo, useMemo, useState } from "react";
import { Flex } from "../../../../flex";
import { RedoList, RedoListItem } from "../../../list/redo-list";
import { DateVariant } from "../utils/date-picker-utils";

interface DateOptionsPanelProps {
  onVariantSelect: (variant: DateVariant) => void;
  allowNoDateOption?: boolean;
}

interface DateOption {
  label: string;
  variant: DateVariant;
}

/**
 * Get date options for quick selection
 */
function dateOptions(t: Translate): DateOption[] {
  return [
    {
      label: t("No date", { context: "date picker preset" }),
      variant: "no-date",
    },
    { label: t("Today", { context: "date picker preset" }), variant: "today" },
    {
      label: t("Yesterday", { context: "date picker preset" }),
      variant: "yesterday",
    },
    {
      label: t("This week", { context: "date picker preset" }),
      variant: "this-week",
    },
    {
      label: t("Last week", { context: "date picker preset" }),
      variant: "last-week",
    },
    {
      label: t("This month", { context: "date picker preset" }),
      variant: "this-month",
    },
    {
      label: t("Last month", { context: "date picker preset" }),
      variant: "last-month",
    },
    {
      label: t("Last 30 days", { context: "date picker preset" }),
      variant: "last-30-days",
    },
    {
      label: t("This year", { context: "date picker preset" }),
      variant: "this-year",
    },
    {
      label: t("Last year", { context: "date picker preset" }),
      variant: "last-year",
    },
  ];
}

export const DateOptionsPanel = memo(function DateOptionsPanel({
  onVariantSelect,
  allowNoDateOption,
}: DateOptionsPanelProps) {
  const { t } = useTranslation();
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );

  const listItems: RedoListItem<DateOption>[] = useMemo(() => {
    return dateOptions(t)
      .filter((option) => allowNoDateOption || option.variant !== "no-date")
      .map((option, index) => {
        return {
          id: `date-option-${index}`,
          type: "text",
          text: option.label,
          value: option,
        };
      });
  }, [allowNoDateOption, t]);

  // Handle item selection
  const itemSelected = useMemo(() => {
    return ({ item }: { item: RedoListItem<DateOption> }) => {
      const selectedOption = item.value;
      if (selectedOption) {
        onVariantSelect(selectedOption.variant);
      }
    };
  }, [onVariantSelect]);

  return (
    <Flex
      borderColor="secondary"
      borderRightWidth="1px"
      borderStyle="solid"
      dir="column"
      p="md"
      style={{ minWidth: "160px" }}
    >
      <Flex>
        <RedoList
          card={false}
          focusedIndex={focusedIndex}
          items={listItems}
          itemSelected={itemSelected}
          refToListenTo={null}
          selectionVariant="none"
          setFocusedIndex={setFocusedIndex}
          size="lg"
        />
      </Flex>
    </Flex>
  );
});
